import React, { useState, useEffect } from 'react';
import MetaTags from 'react-meta-tags';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Button, Row, Col, Card, CardBody } from 'reactstrap';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';

import '../../assets/scss/datatables.scss';
import { getFirebaseBackend } from '../../helpers/firebaseHelper';
import { getPendingUserColumns } from './config/userTableColumns';
import { getDefaultSorted, getPageOptions } from './config/tableConfig';
import ActionButtons from './components/Action-Buttons';

const ListPending = () => {
  const firebaseHelper = getFirebaseBackend();
  const { t: translate } = useTranslation();
  const { SearchBar } = Search;
  const [userData, setUserData] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);

  const fetchAllUsers = async () => {
    const users = await firebaseHelper.getAllUsers();
    setPendingUsers(users);
  };

  const fetchPending = async () => {
    const newUserData = await Promise.all(
      Object.values(pendingUsers)
        .filter(isPending)
        .map(async (userInfo) => {
          const account = await firebaseHelper.accountName(userInfo.account);

          return {
            id: userInfo.id,
            name: `${userInfo.name} ${userInfo.lastName}`,
            email: userInfo.email,
            account: account,
            createdAt: new Date(userInfo.createdAt).toLocaleDateString(),
            action: (
              <ActionButtons
                userInfo={userInfo}
                onAcceptClick={onAcceptClick}
                onDenyClick={onDenyClick}
              />
            ),
          };
        }),
    );
    setUserData(newUserData);
  };

  const deactivatedTotal = (acc, cur) => (cur.status === 'Deactivated' ? ++acc : acc);
  const pendingTotal = (acc, cur) => (cur.status === 'Pending' ? ++acc : acc);
  const activatedTotal = (acc, cur) => (cur.status === 'Activated' ? ++acc : acc);

  const isPending = (value) => {
    return value.status === 'Pending';
  };

  const onAcceptClick = async (userInfo) => {
    try {
      await firebaseHelper.activateUser(userInfo.id);
      fetchAllUsers();
    } catch (error) {
      console.error(error);
    }
  };

  const onDenyClick = async (userInfo) => {
    try {
      await firebaseHelper.deactivateUser(userInfo.id);
      fetchAllUsers();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      if (!pendingUsers) return;

      await fetchPending();
    };
    initialize();
  }, [pendingUsers]);

  useEffect(() => {
    fetchAllUsers();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content" style={{ minHeight: '100vh' }}>
        <MetaTags>
          <title>{translate('List Pending Users')} | Ectools</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col xs="12" className="mb-4">
              <h4 className="mb-0 font-size-18">
                {translate('Pending Users')} (
                {pendingUsers !== null ? Object.values(pendingUsers).reduce(pendingTotal, 0) : 0})
              </h4>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(getPageOptions(userData.length))}
                    keyField="id"
                    columns={getPendingUserColumns(translate)}
                    data={userData}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        columns={getPendingUserColumns(translate)}
                        data={userData}
                        search
                      >
                        {(toolkitProps) => (
                          <React.Fragment>
                            <Row className="mb-2 d-flex justify-content-between">
                              <Col md="4">
                                <div className="search-box me-2 mb-2 d-inline-block">
                                  <div className="position-relative">
                                    <SearchBar
                                      {...toolkitProps.searchProps}
                                      placeholder={translate('Search')}
                                    />
                                    <i className="bx bx-search-alt search-icon" />
                                  </div>
                                </div>
                              </Col>
                              <Col md="8" className="d-flex flex-row-reverse align-items-center">
                                <Button
                                  color="primary"
                                  className="d-flex gap-2 align-items-center"
                                  tag={Link}
                                  to="/create-user"
                                >
                                  <i className="bx bx-plus-circle" />
                                  {translate('Create new User')}
                                </Button>
                                <Link to="/list-users">
                                  <p className="mx-4 mb-0" style={{ color: '#ADB5BD' }}>
                                    {translate('See all users')} (
                                    {pendingUsers !== null
                                      ? Object.values(pendingUsers).reduce(activatedTotal, 0)
                                      : 0}
                                    )
                                  </p>
                                </Link>
                                <Link to="/list-deactivated">
                                  <p className="mx-4 mb-0" style={{ color: '#ADB5BD' }}>
                                    {translate('Deactivated Users')} (
                                    {pendingUsers !== null
                                      ? Object.values(pendingUsers).reduce(deactivatedTotal, 0)
                                      : 0}
                                    )
                                  </p>
                                </Link>
                              </Col>
                            </Row>

                            <Row>
                              <Col xl="12">
                                <div className="table-responsive">
                                  <BootstrapTable
                                    keyField={'id'}
                                    responsive
                                    hover
                                    bordered={false}
                                    striped={false}
                                    defaultSorted={getDefaultSorted}
                                    classes={'table align-middle table-nowrap'}
                                    headerWrapperClasses={'thead-light'}
                                    {...toolkitProps.baseProps}
                                    {...paginationTableProps}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <Row className="align-items-md-center mt-30">
                              <Col className="inner-custom-pagination d-flex">
                                <div className="d-inline">
                                  <SizePerPageDropdownStandalone {...paginationProps} />
                                </div>
                                <div className="text-md-right ms-auto">
                                  <PaginationListStandalone {...paginationProps} />
                                </div>
                              </Col>
                            </Row>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ListPending;
